import { PageType } from 'lib/dot/enum/pageType.ts';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import type { BooleanAnalytics, LocalityAnalytics, PriceAnalytics } from 'lib/dot/types.ts';
import type { OfferData, OfferStatus } from 'service/offer/model.ts';
import { offerToLocalityAnalytics } from 'lib/dot/utils.ts';

export type SaveOfferAnalytics = {
	action: 'saveAdvert';
	actionType: 'select' | 'deselect';
	advertAge: number;
	advertId: number;
	advertPosition?: string;
	advertStatus: OfferStatus;
	categoryCount?: number;
	categoryId: number;
	detailId?: number;
	itemCount?: number;
	locality?: LocalityAnalytics[];
	pageType: PageType;
	price: PriceAnalytics;
	sellerId: number;
	topped: BooleanAnalytics;
};

// #if [DEV]
const SAVE_OFFER_KEYS = ['pageType'];
// #endif

export type SendSaveOfferAnalyticsAdditionalData = Pick<
	SaveOfferAnalytics,
	'itemCount' | 'categoryCount' | 'advertPosition'
>;

export function sendSaveOfferAnalytics(
	offer: OfferData,
	favorite: boolean,
	{ categoryCount, itemCount, advertPosition }: SendSaveOfferAnalyticsAdditionalData = {},
) {
	// #if [DEV]
	DOTHelper.checkStore(SAVE_OFFER_KEYS);
	// #endif

	const store = DOTHelper.getStore();
	const _itemCount = itemCount || store.itemCount;

	const additionalData = {
		...(advertPosition && { advertPosition: advertPosition }),
		...(categoryCount && { categoryCount: categoryCount }),
		...(_itemCount && { itemCount: _itemCount }),
	};

	const data: SaveOfferAnalytics = {
		action: 'saveAdvert',
		actionType: favorite ? 'select' : 'deselect',
		advertAge: Math.floor(
			(Date.now() - new Date(offer.editDate ?? offer.createDate).getTime()) / (1000 * 60 * 60 * 24),
		), // In days since last edit
		advertId: offer.id,
		advertStatus: offer.status ?? 'active', // Not all endpoints (search endpoints) return status. It is reasonable to assume that since the search returned the offer that it is, in fact, active.
		categoryId: offer.category.id,
		detailId: store.id,
		locality: offerToLocalityAnalytics(offer, store.matchedLocalityId),
		pageType: store.pageType,
		price: offer.priceByAgreement ? 'dohodou' : offer.price,
		sellerId: (offer?.user?.id ?? offer?.premise?.id)!,
		topped: DOTHelper.sanitizeBoolean(offer.topped),
		...additionalData,
	};

	return DOTHelper.hit(data);
}
